// Pure math behind the Financial Assessment. Everything here is deterministic:
// same store plus same assumptions gives the same numbers, which is the bar the
// deliverable is accepted against. No growth rate, no randomness, no clock
// except the `asOf` argument, so every figure on the page can be re-derived by
// hand from the questionnaire.
//
// The dashboard selectors answer "what is true today". These answer "what
// happens over time", so the gating rules are extended rather than reused
// wholesale: income has to turn OFF at retirement as well as on at a claiming
// age, and expenses have to honor endDate (getIncludedExpenseRows deliberately
// does not, see its comment there).

import { getDashboardMetrics, ownerPerson } from './selectors.js';
import { estateDocCodes } from './estateDocs.js';

function toNumber(v) {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
}

function fullName(first, last) {
  return [(first || '').trim(), (last || '').trim()].filter(Boolean).join(' ');
}

// Parse YYYY-MM-DD by parts. `new Date('YYYY-MM-DD')` is UTC midnight, which
// shifts a day backwards in Pacific time and moved a start date across a year
// boundary. Same reasoning as isFutureDate in selectors.js.
function parseDateParts(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate() };
}

function yearOf(dateStr) {
  const parts = parseDateParts(dateStr);
  return parts ? parts.y : null;
}

const ASSUMPTION_KEYS = [
  'c1RetireAge', 'c2RetireAge', 'annualSpending', 'annualSavings',
  // The Roth conversion lever. Defaults to nothing converted, so the report
  // opens describing the plan as it stands rather than as we would like it.
  'rothAnnual', 'rothYears',
];

/**
 * What the plan says today, before the client moves any slider. Each value is a
 * number or null; null means "the questionnaire does not say", and the UI shows
 * the slider at a sensible position without claiming the client told us.
 */
/**
 * Age on a given date, in whole years. Null unless both dates are real and the
 * date is not before the birth date.
 */
function ageOn(dob, asOfParts) {
  const b = parseDateParts(dob);
  if (!b || !asOfParts) return null;
  let years = asOfParts.y - b.y;
  if (asOfParts.mo < b.mo || (asOfParts.mo === b.mo && asOfParts.d < b.d)) years -= 1;
  return years >= 0 ? years : null;
}

/**
 * The retirement age for one person, however the questionnaire recorded it.
 *
 * The PQ takes an age OR a date and never both: the Plan Order Form has worked
 * that way since 2026-09-14, and the employment section greys the age box the
 * moment a date is entered. This read only ever looked at the age, so a
 * household that gave a date had no retirement age at all, employment ran to
 * the end of the timeline, and the goal sheet then reported the client was
 * already retired with no shortfall. All three symptoms from one missing read.
 *
 * Returns { age, source }: `source` is 'age' when the advisor typed one,
 * 'date' when it was worked out from the date and the birth date, and
 * 'unresolved' when the questionnaire says a date but gives us no birth date
 * to measure it against. Unresolved is NOT null: null means "no answer given",
 * which is an ordinary state for someone already retired, while unresolved
 * means "an answer was given and we could not use it" and has to be visible
 * rather than guessed at.
 */
function retirementAgeFor(data, who) {
  const person = data?.employment?.[who] || {};
  const family = data?.family || {};

  const typed = toNumber(person.retirementAge);
  if (typed > 0) return { age: typed, source: 'age' };

  const dateParts = parseDateParts(person.retirementDate);
  if (!dateParts) return { age: null, source: null };

  const dob = family[`${who}DOB`];
  const fromDob = ageOn(dob, dateParts);
  if (fromDob !== null) return { age: fromDob, source: 'date' };

  // No date of birth, but a current age plus a retirement year still pins it:
  // someone who is 60 now and retires in 2031 retires at about 65. Whole years
  // only, and deliberately not rounded, because the year the income stops is
  // what the timeline needs.
  const ageNow = toNumber(family[`${who}Age`]);
  if (ageNow > 0) {
    const asOfYear = new Date().getFullYear();
    const derived = ageNow + (dateParts.y - asOfYear);
    if (derived > 0) return { age: derived, source: 'date' };
  }

  return { age: null, source: 'unresolved' };
}

export function getAssessmentDefaults(data) {
  const emp = data?.employment || {};
  const te = data?.taxesExpenses || {};
  const metrics = getDashboardMetrics(data);

  const retireAge = who => retirementAgeFor(data, who).age;

  // Base spending is the standing figure plus the expense rows that are not
  // dated. Dated rows are added per year by buildIncomeTimeline instead, so a
  // college bill starting in 2028 is not folded into "what they spend now".
  const rows = Array.isArray(te.expenses) ? te.expenses : [];
  const undatedRows = rows.filter(r => r && !r.startDate && !r.endDate);
  const baseSpending = toNumber(te.livingExpenses)
    + undatedRows.reduce((s, r) => s + toNumber(r.amount), 0);

  return {
    c1RetireAge: retireAge('client1'),
    c2RetireAge: retireAge('client2'),
    // Which of them gave a retirement answer we could not use. Carried through
    // the assumptions so the page can say so instead of quietly modelling
    // someone as working forever.
    unresolvedRetirement: ['client1', 'client2'].filter(
      who => retirementAgeFor(data, who).source === 'unresolved'
    ),
    annualSpending: baseSpending,
    annualSavings: metrics.totalSavings,
    // Nothing converted until somebody moves the slider. The report describes
    // the plan the client actually has before it describes a better one.
    rothAnnual: 0,
    rothYears: 0,
  };
}

/**
 * The single object every downstream calculation reads. What-if values override
 * the plan only where the client actually moved something; null falls through
 * to the default. Routing everything through here is what makes "move a slider,
 * then move it back" return to exactly the original numbers.
 */
export function getAssessmentAssumptions(data) {
  const defaults = getAssessmentDefaults(data);
  const whatIf = data?.assessment?.whatIf || {};
  const merged = { ...defaults };
  for (const key of ASSUMPTION_KEYS) {
    const v = whatIf[key];
    if (v !== null && v !== undefined && v !== '') merged[key] = toNumber(v);
  }
  return merged;
}

/**
 * Year-by-year income against spending need, from `asOf` until the YOUNGER of
 * the two (or the only client) would reach horizonAge, so no part of either
 * life is outside a "savings last" statement.
 *
 * Gating, mirroring getIncludedIncomeRows and extending it through time:
 *  - employment stops once its owner reaches the assumed retirement age, but
 *    always counts in the current year (the row existing asserts they are
 *    working now, so year zero stays consistent with the PQ headline);
 *  - Social Security starts at the owner's `startingAge`; a row without one
 *    counts throughout, same as notYetByAge treating 0 as "no gate";
 *  - pensions honor `startDate` and `startAge`, either one sufficing to delay;
 *  - other income honors its startDate/endDate window, which is how a dated
 *    windfall is modelled;
 *  - an owner matching neither client falls back to client 1, because a pension
 *    owner is often the payer rather than a person.
 */
export function buildIncomeTimeline(data, assumptions, options = {}) {
  const { horizonAge = 95, asOf = new Date() } = options;
  const income = data?.income || {};
  const family = data?.family || {};
  const te = data?.taxesExpenses || {};

  const currentYear = asOf.getFullYear();
  const c1AgeNow = toNumber(family.client1Age);
  const c2AgeNow = toNumber(family.client2Age);
  const c2Name = fullName(family.client2FirstName, family.client2LastName);
  const hasC2 = Boolean(c2Name) && c2AgeNow > 0;

  // Same owner match as current income, so a spouse named by first name ages
  // as the spouse here too.
  const isClient2 = row => hasC2 && ownerPerson(family, row?.owner) === 'client2';
  const ageIn = (row, year) =>
    (isClient2(row) ? c2AgeNow : c1AgeNow) + (year - currentYear);
  const retireAgeFor = row =>
    isClient2(row) ? assumptions.c2RetireAge : assumptions.c1RetireAge;

  // The timeline runs until the YOUNGER of the two reaches horizonAge. It used
  // to stop at the older person's, which for a 70-year-old with a 58-year-old
  // spouse left twelve years of the younger person's life off every chart and
  // every coverage figure. An unknown age does not shorten it.
  const known = [c1AgeNow, hasC2 ? c2AgeNow : 0].filter(a => a > 0);
  const youngestNow = known.length ? Math.min(...known) : 0;
  const years = youngestNow > 0 ? Math.max(1, Math.round(horizonAge - youngestNow) + 1) : 1;

  const rowsOf = key => (Array.isArray(income[key]) ? income[key].filter(Boolean) : []);
  // With each row's place in the stored table, so the goal sheet can list a
  // pension or another income on its own line and write a change back to it.
  const placedOf = key => (Array.isArray(income[key]) ? income[key] : []).map((r, index) => ({ r, index })).filter(x => x.r);
  const employment = rowsOf('employment');
  const socialSecurity = rowsOf('socialSecurity');
  const pension = placedOf('pension');
  const other = placedOf('other');
  const expenseRows = Array.isArray(te.expenses) ? te.expenses.filter(Boolean) : [];

  const timeline = [];
  for (let i = 0; i < years; i += 1) {
    const year = currentYear + i;

    let employmentIncome = 0;
    employment.forEach(r => {
      const retireAge = retireAgeFor(r);
      const stillWorking = i === 0 || !(retireAge > 0) || ageIn(r, year) < retireAge;
      if (stillWorking) employmentIncome += toNumber(r.annualAmount);
    });

    let ssIncome = 0;
    socialSecurity.forEach(r => {
      const startAge = toNumber(r.startingAge);
      if (!(startAge > 0) || ageIn(r, year) >= startAge) ssIncome += toNumber(r.annualAmount);
    });

    let pensionIncome = 0;
    const pensionRows = pension.map(({ r, index }) => {
      const startYear = yearOf(r.startDate);
      const startAge = toNumber(r.startAge);
      const dateOk = startYear === null || year >= startYear;
      const ageOk = !(startAge > 0) || ageIn(r, year) >= startAge;
      const paying = dateOk && ageOk;
      const amount = paying ? toNumber(r.annualAmount) : 0;
      pensionIncome += amount;
      return { index, amount, paying };
    });

    let otherIncome = 0;
    const otherRows = other.map(({ r, index }) => {
      const startYear = yearOf(r.startDate);
      const endYear = yearOf(r.endDate);
      const started = startYear === null || year >= startYear;
      const ended = endYear !== null && year > endYear;
      const paying = started && !ended;
      const amount = paying ? toNumber(r.annualAmount) : 0;
      otherIncome += amount;
      return { index, amount, paying };
    });

    let spendingNeed = toNumber(assumptions.annualSpending);
    expenseRows.forEach(r => {
      // Undated rows are already inside the base spending assumption.
      if (!r.startDate && !r.endDate) return;
      const startYear = yearOf(r.startDate);
      const endYear = yearOf(r.endDate);
      const started = startYear === null || year >= startYear;
      const ended = endYear !== null && year > endYear;
      if (started && !ended) spendingNeed += toNumber(r.amount);
    });

    const bySource = {
      employment: employmentIncome,
      socialSecurity: ssIncome,
      pension: pensionIncome,
      other: otherIncome,
    };
    const totalIncome = employmentIncome + ssIncome + pensionIncome + otherIncome;

    timeline.push({
      year,
      c1Age: c1AgeNow > 0 ? c1AgeNow + i : null,
      c2Age: hasC2 ? c2AgeNow + i : null,
      bySource,
      byRow: { pension: pensionRows, other: otherRows },
      totalIncome,
      spendingNeed,
      gap: spendingNeed - totalIncome,
    });
  }

  return timeline;
}

/**
 * Headline figures drawn off the timeline. The gap is deliberately "spending
 * need against guaranteed income", before tax and before anything is drawn from
 * the portfolio: that is what the assessment is asking, and it is why this
 * number is not the dashboard's cash flow and must never be labelled as it.
 *
 * Coverage walks the liquid pool down year by year with no assumed growth. That
 * understates a real portfolio on purpose, so every figure is checkable by hand
 * and no return assumption has to be defended in front of a prospect.
 */
export function getGapSummary(timeline, options = {}) {
  // Client 1's first name, so a run-out year can say whose age it names.
  const { client1Name = null } = options;
  const { liquidAssets = 0, assumptions = {} } = options;
  const rows = Array.isArray(timeline) ? timeline : [];
  if (!rows.length) {
    return {
      gapNow: 0, gapAfterStepDown: null, gapAtRetirement: null,
      firstShortfallYear: null, peakGap: 0,
      coverage: { yearsCovered: null, depletionAge: null, neverDepletes: true },
    };
  }

  const gapNow = rows[0].gap;

  // The first year the need changes is the step-down (or step-up) the client
  // described, for example a spending drop once a mortgage is cleared.
  let gapAfterStepDown = null;
  const firstChange = rows.find(r => r.spendingNeed !== rows[0].spendingNeed);
  if (firstChange) gapAfterStepDown = firstChange.gap;

  // Retirement here means the year the last earner stops, which is the year
  // employment income first reaches zero having started above it.
  let gapAtRetirement = null;
  if (rows[0].bySource.employment > 0) {
    const retired = rows.find(r => r.bySource.employment === 0);
    if (retired) gapAtRetirement = retired.gap;
  }

  const shortfallRow = rows.find(r => r.gap > 0);
  const firstShortfallYear = shortfallRow ? shortfallRow.year : null;
  const peakGap = rows.reduce((max, r) => Math.max(max, r.gap), 0);

  let pool = toNumber(liquidAssets);
  const savings = toNumber(assumptions.annualSavings);
  let yearsCovered = 0;
  let depletionRow = null;
  for (const row of rows) {
    // While still earning, surplus savings keep adding to the pool.
    if (row.gap <= 0) {
      if (row.bySource.employment > 0) pool += savings;
    } else {
      pool -= row.gap;
    }
    if (pool < 0) { depletionRow = row; break; }
    yearsCovered += 1;
  }

  return {
    gapNow,
    gapAfterStepDown,
    gapAtRetirement,
    firstShortfallYear,
    peakGap,
    coverage: {
      yearsCovered,
      // Client 1's age that year, and their name when the caller gives it, so
      // the page can say whose age it is: for a couple, a bare age is ambiguous.
      depletionAge: depletionRow ? depletionRow.c1Age : null,
      depletionName: depletionRow && client1Name ? client1Name : null,
      depletionYear: depletionRow ? depletionRow.year : null,
      neverDepletes: !depletionRow,
    },
  };
}

/**
 * The three tax pools, delegating to getDashboardMetrics so the assessment can
 * never disagree with the edit rail or the presented sheet, plus the account
 * rows behind each pool for display.
 */
export function getTaxPoolSnapshot(data) {
  const m = getDashboardMetrics(data);
  const assets = data?.assets || {};
  const rowsFrom = (...keys) => {
    const out = [];
    keys.forEach(key => {
      const rows = Array.isArray(assets[key]) ? assets[key] : [];
      rows.forEach(r => {
        if (!r) return;
        const value = toNumber(r.marketValue);
        if (!value) return;
        out.push({
          label: (r.custodian || r.description || '').trim() || 'Account',
          type: (r.type || r.rothType || '').trim(),
          owner: (r.ownership || '').trim(),
          value,
          table: key,
        });
      });
    });
    return out.sort((a, b) => b.value - a.value);
  };

  const pools = [
    { key: 'taxDeferred', label: 'Tax-deferred', value: m.taxDeferred, rows: rowsFrom('taxDeferred') },
    { key: 'taxFree', label: 'Tax-free', value: m.taxFree, rows: rowsFrom('roth', 'hsa') },
    { key: 'taxable', label: 'Taxable', value: m.taxable, rows: rowsFrom('taxable', 'cashCd') },
  ];
  const total = pools.reduce((s, p) => s + p.value, 0);

  return {
    pools,
    total,
    // Share of liquid wealth that is taxed as ordinary income on the way out.
    // Usually the single most persuasive number on the page.
    deferredSharePct: total > 0 ? (m.taxDeferred / total) * 100 : 0,
  };
}

/**
 * Where the holdings analysis comes from, in priority order: a live result from
 * the statement pipeline, then a fixture keyed to this household (the demo
 * path), then the generic sample when explicitly asked for. Null means no
 * statement has been analyzed yet, which the UI renders as an invitation rather
 * than as zeroes.
 */
export function getHoldingsBundle(data, options = {}) {
  const { allowSample = false, fixturesByLead = {}, sample = null } = options;
  const live = data?.assessment?.holdings;
  if (live && Array.isArray(live.holdings) && live.holdings.length) return live;
  const uid = data?._uid || data?._leadId;
  if (uid && fixturesByLead[uid]) return fixturesByLead[uid];
  if (allowSample && sample) return sample;
  return null;
}

/**
 * Fee analysis. Expense ratios are a percentage, so 0.04 means four basis
 * points of the position per year. A stock genuinely has no expense ratio, so 0
 * is a real value and stays in the average; null means we could not determine
 * it and is excluded from the weighted denominator rather than being counted as
 * free.
 */
export function analyzeFees(bundle) {
  const holdings = Array.isArray(bundle?.holdings) ? bundle.holdings.filter(Boolean) : [];
  if (!holdings.length) return null;

  const totalValue = holdings.reduce((s, h) => s + toNumber(h.value), 0);
  let knownValue = 0;
  let weightedCost = 0;

  const perHolding = holdings.map(h => {
    const value = toNumber(h.value);
    const hasEr = h.expenseRatioPct !== null && h.expenseRatioPct !== undefined && h.expenseRatioPct !== '';
    const er = hasEr ? toNumber(h.expenseRatioPct) : null;
    const feeDollars = hasEr ? value * (er / 100) : null;
    if (hasEr) {
      knownValue += value;
      weightedCost += value * (er / 100);
    }
    return {
      ...h,
      value,
      weightPct: totalValue > 0 ? (value / totalValue) * 100 : 0,
      expenseRatioPct: er,
      feeDollars,
      expenseRatioKnown: hasEr,
    };
  }).sort((a, b) => b.value - a.value);

  const largest = perHolding[0] || null;

  const perPool = {};
  perHolding.forEach(h => {
    const pool = h.pool || 'unclassified';
    if (!perPool[pool]) perPool[pool] = { value: 0, feeDollars: 0 };
    perPool[pool].value += h.value;
    perPool[pool].feeDollars += h.feeDollars || 0;
  });

  return {
    totalValue,
    // Weighted across the positions we can price, so an unknown ratio cannot
    // quietly drag the average toward zero.
    weightedExpenseRatioPct: knownValue > 0 ? (weightedCost / knownValue) * 100 : null,
    annualFeeDollars: weightedCost,
    coveragePct: totalValue > 0 ? (knownValue / totalValue) * 100 : 0,
    perHolding,
    perPool,
    flags: {
      largestHolding: largest,
      // A single position dominating the portfolio is a conversation the
      // advisor wants to have, and it is usually company stock.
      singleStockConcentrationPct: largest && largest.assetClass === 'Single Stock'
        ? largest.weightPct
        : perHolding.filter(h => h.assetClass === 'Single Stock')
            .reduce((max, h) => Math.max(max, h.weightPct), 0),
      // Positions we could price but whose expense ratio we could not determine.
      // Deliberately NOT the same thing as bundle.unpricedHoldings, which is a
      // position with no priceable security id at all.
      unknownExpenseRatioCount: perHolding.filter(h => !h.expenseRatioKnown).length,
    },
  };
}

/**
 * Positions carrying a real market value with no priceable security id behind
 * them: annuity contracts, separate accounts, private funds. They are inside
 * the household total and outside everything YCharts prices, so every
 * portfolio-level cost, return and risk figure covers less than the stated
 * total. An annuity of exactly this shape reached a delivered client report
 * once because nothing on the page mentioned it.
 *
 * Null when there are none, so callers render nothing rather than an empty box.
 */
export function summarizeUnpricedHoldings(bundle) {
  const rows = Array.isArray(bundle?.unpricedHoldings)
    ? bundle.unpricedHoldings.filter(Boolean)
    : [];
  if (!rows.length) return null;

  const unpricedValue = rows.reduce((s, h) => s + toNumber(h.value), 0);
  const priced = Array.isArray(bundle?.holdings) ? bundle.holdings.filter(Boolean) : [];
  const pricedValue = priced.reduce((s, h) => s + toNumber(h.value), 0);
  // The backend's stated total wins, so the page never quietly re-derives the
  // household's own number. Falls back to the two parts when it is absent.
  const stated = toNumber(bundle?.portfolio?.totalValue);
  const totalValue = stated > 0 ? stated : pricedValue + unpricedValue;

  return {
    rows: rows
      .map(h => ({
        account: (h.account || '').trim(),
        name: (h.name || '').trim(),
        ticker: (h.ticker || '').trim(),
        pool: (h.pool || '').trim(),
        value: toNumber(h.value),
      }))
      .sort((a, b) => b.value - a.value),
    count: rows.length,
    unpricedValue,
    pricedValue,
    totalValue,
    pricedSharePct: totalValue > 0 ? (pricedValue / totalValue) * 100 : null,
  };
}

/**
 * The two-pass extraction result. Extraction from a PDF is not deterministic:
 * the same statement run four times gave three different answers, once dropping
 * a six-figure annuity and once duplicating every position. The backend now
 * reads twice, takes the union and reports every disagreement; this shapes that
 * for the planner-facing review.
 *
 * Null when the bundle predates the double read, so an older analysis shows
 * nothing rather than an unearned reassurance.
 */
export function getExtractionVerification(bundle) {
  const v = bundle?.verification;
  if (!v || typeof v !== 'object') return null;

  const discrepancies = Array.isArray(v.discrepancies) ? v.discrepancies.filter(Boolean) : [];
  const collapsedDuplicates = Array.isArray(v.collapsedDuplicates)
    ? v.collapsedDuplicates.filter(Boolean)
    : [];

  return {
    mode: v.mode === 'double' ? 'double' : 'single',
    doubleChecked: v.mode === 'double',
    agreed: v.agreed !== false,
    discrepancies,
    collapsedDuplicates,
    // Anything the two reads did not agree on has to be checked against the
    // statement by hand, so this is what decides whether the review opens by
    // itself rather than waiting to be clicked.
    needsReview: v.agreed === false
      || discrepancies.length > 0
      || collapsedDuplicates.length > 0,
  };
}

/**
 * Per-account totals for the reconciliation table. `weightComplete === false`
 * means the statement's own percentages do not add to 100, which almost always
 * means a position in that account was missed.
 */
export function getAccountReconciliation(bundle) {
  const rows = Array.isArray(bundle?.accounts) ? bundle.accounts.filter(Boolean) : [];
  if (!rows.length) return null;

  return rows.map(a => ({
    account: (a.account || '').trim() || 'Account',
    asOf: a.asOf || null,
    value: toNumber(a.value),
    positions: toNumber(a.positions),
    weightSumPct: a.weightSumPct === null || a.weightSumPct === undefined
      ? null
      : toNumber(a.weightSumPct),
    weightComplete: typeof a.weightComplete === 'boolean' ? a.weightComplete : null,
  }));
}

// ---------------------------------------------------------------------------
// Required minimum distributions
// ---------------------------------------------------------------------------

/**
 * IRS Uniform Lifetime Table, the divisors in force since 2022.
 *
 * These are the published figures, not a model. The RMD for a year is the
 * account balance divided by the divisor for that age.
 *
 * Worth knowing before reading anything into the shape of the schedule: from 73
 * to 74 the divisor drops by exactly 1.0, which under zero growth makes those
 * two withdrawals identical to the cent, and after that it drops by less than 1
 * each year, so the withdrawal drifts slowly DOWN. Forced withdrawals climbing
 * over time is a growth effect, not a divisor effect. Since this assessment
 * assumes no growth, the schedule it prints is close to flat, and that is the
 * honest shape rather than a scary one.
 */
const UNIFORM_LIFETIME = {
  73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1,
  80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2,
  87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1,
  94: 9.5, 95: 8.9, 96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4,
};

/**
 * When RMDs begin, under SECURE 2.0.
 *
 * The age moved to 73, and moves again to 75 for anyone born in 1960 or later.
 * Getting this wrong by two years moves the whole schedule, so it is a rule
 * rather than a constant.
 */
export function rmdStartAge(birthYear) {
  return birthYear >= 1960 ? 75 : 73;
}

/**
 * What the tax-deferred pool will force out, and when.
 *
 * The argument this makes is not that RMDs are bad. It is that the timing stops
 * being a choice: at some point the account distributes whether or not the
 * money is wanted that year, and it lands as ordinary income on top of whatever
 * else is coming in. A client looking at a seven-figure 401(k) has almost never
 * seen that figure written down.
 *
 * Deliberately assumes ZERO GROWTH, like everything else in this assessment.
 * A real account earning anything at all produces a bigger balance and bigger
 * forced withdrawals, so every figure here is the floor. Saying so is the
 * point: it cannot be waved away as a projection resting on a market
 * assumption, because there is no market assumption in it.
 *
 * Treats the household's tax-deferred savings as one pool timed off the older
 * client. RMDs are really per person per account; splitting them needs an owner
 * on every account, which the questionnaire does not reliably carry.
 */
/**
 * Walk the withdrawals from `startAge` to the horizon on a given balance.
 *
 * Extracted so a Roth conversion can re-run exactly the same arithmetic on a
 * smaller balance. If the two ever diverged, the "before" and "after" figures
 * on the page would not be comparable, and the whole point of the slider is
 * that they are.
 */
function walkRmdSchedule(startBalance, { startAge, ageNow, currentYear, horizonAge }) {
  let balance = Math.max(0, startBalance);
  const schedule = [];
  let total = 0;
  for (let age = startAge; age <= horizonAge; age += 1) {
    const divisor = UNIFORM_LIFETIME[age];
    if (!divisor) break;
    const amount = balance / divisor;
    schedule.push({
      age,
      year: currentYear + (age - ageNow),
      divisor,
      amount,
      balanceBefore: balance,
    });
    total += amount;
    balance -= amount;
  }
  return { schedule, total, remaining: balance };
}

export function getRmdOutlook(data, options = {}) {
  const { horizonAge = 95, asOf = new Date() } = options;
  const snapshot = getTaxPoolSnapshot(data);
  const deferred = snapshot.pools.find(p => p.key === 'taxDeferred');
  const balance0 = deferred ? deferred.value : 0;
  if (!(balance0 > 0)) return null;

  const family = data?.family || {};
  const c1Age = toNumber(family.client1Age);
  const c2Age = toNumber(family.client2Age);
  const c1Name = fullName(family.client1FirstName, family.client1LastName);
  const c2Name = fullName(family.client2FirstName, family.client2LastName);
  const useC2 = c2Age > c1Age;
  const ageNow = Math.max(c1Age, c2Age);
  if (!(ageNow > 0)) return null;

  const currentYear = asOf.getFullYear();
  // Age is what the questionnaire carries, so the birth year is inferred and
  // can be a year out for anyone who has not had their birthday yet. It only
  // matters within a year of the 1960 boundary, and erring toward the earlier
  // start age is the conservative direction.
  const birthYear = currentYear - ageNow;
  const startAge = rmdStartAge(birthYear);
  const alreadyStarted = ageNow >= startAge;

  // Two people means the pool is timed off the older of them and attributed to
  // them entirely. Real distributions are per person per account, and the
  // questionnaire's ownership is a free-text first name, so this is an
  // assumption the page has to state rather than a fact it can assert.
  const twoPeople = Boolean(c1Name && c2Name && c1Age > 0 && c2Age > 0);

  // ALREADY TAKING THEM: no figure, only the question.
  //
  // The schedule below starts at `startAge` and walks the CURRENT balance
  // forward. For someone who passed that age years ago it reconstructs a past
  // schedule: an 80-year-old with $1m produced a first withdrawal of $37,736
  // dated 2019, which the page then labelled "Required this year". Every
  // number downstream inherits the error, because the balance it walks has
  // already had real withdrawals taken out of it.
  //
  // Getting this right needs the prior 31 December balance per account, the
  // owner of each account, and a date of birth. The questionnaire carries none
  // of the three. So the honest output is the pool and the question, and the
  // figures stay in the plan that can price them.
  if (alreadyStarted) {
    return {
      ownerName: twoPeople ? '' : (useC2 ? c2Name : c1Name),
      ageNow,
      startAge,
      yearsAway: 0,
      alreadyStarted: true,
      balance: balance0,
      ownershipAssumed: twoPeople,
      // Everything a caller might render as a number is deliberately absent.
      schedule: [],
    };
  }

  const { schedule, total, remaining: leftAtHorizon } =
    walkRmdSchedule(balance0, { startAge, ageNow, currentYear, horizonAge });
  if (!schedule.length) return null;
  const balance = leftAtHorizon;

  const first = schedule[0];
  const peak = schedule.reduce((a, b) => (b.amount > a.amount ? b : a), schedule[0]);

  return {
    ownerName: useC2 ? c2Name : c1Name,
    ageNow,
    startAge,
    yearsAway: Math.max(0, startAge - ageNow),
    alreadyStarted: false,
    balance: balance0,
    ownershipAssumed: twoPeople,
    firstYear: first.year,
    firstAmount: first.amount,
    peakAmount: peak.amount,
    peakAge: peak.age,
    totalToHorizon: total,
    horizonAge,
    remainingAtHorizon: balance,
    schedule,
  };
}

// ---------------------------------------------------------------------------
// Asset location
// ---------------------------------------------------------------------------

// Which bucket belongs in which pool, and why. This is the settled part of
// asset location, not a house view: interest is taxed as ordinary income as it
// is earned, so bonds are expensive to hold in a taxable account; and a Roth
// dollar is worth most when it holds whatever grows fastest, because that
// growth is never taxed.
const LOCATION_RULES = [
  {
    key: 'bondsInTaxable',
    pool: 'taxable',
    bucket: 'Fixed Income',
    headline: 'Bonds are sitting in your taxable account',
    why: 'Interest is taxed as ordinary income every year, whether or not you spend it. '
      + 'The same bonds held in a tax-deferred account produce the same return with no annual tax bill.',
  },
  {
    key: 'cashInTaxFree',
    pool: 'taxFree',
    bucket: 'Cash',
    headline: 'Cash is sitting in your Roth',
    why: 'A Roth dollar never gets taxed again, which makes it the most valuable place you have to hold growth. '
      + 'Cash is the one thing that does not grow.',
  },
  {
    key: 'cashInTaxDeferred',
    pool: 'taxDeferred',
    bucket: 'Cash',
    headline: 'Cash is sitting in your tax-deferred account',
    why: 'It will still be taxed as ordinary income when it comes out, so it carries the tax treatment of growth without any of the growth.',
  },
];

/**
 * Where each kind of asset is held, against where it is cheapest to hold it.
 *
 * Reads the analyzed statements rather than the questionnaire, because it needs
 * to know what is inside each account and not just what the account is worth.
 * Reports dollars and never a tax saving: the size of the saving depends on a
 * bracket we have not asked for, and an invented rate would be the one number
 * on the page that an advisor could not defend in the meeting.
 */
export function analyzeTaxLocation(bundle) {
  const pools = bundle?.pools;
  if (!pools) return null;

  const dollars = (poolKey, bucket) => {
    const pool = pools[poolKey];
    if (!pool || !(pool.value > 0)) return 0;
    const pct = toNumber(pool.allocation?.[bucket]);
    return pct > 0 ? (pool.value * pct) / 100 : 0;
  };

  const findings = LOCATION_RULES
    .map(rule => ({ ...rule, value: dollars(rule.pool, rule.bucket) }))
    .filter(f => f.value > 0)
    .sort((a, b) => b.value - a.value);

  const totalValue = ['taxDeferred', 'taxFree', 'taxable']
    .reduce((sum, k) => sum + (toNumber(pools[k]?.value) || 0), 0);

  return {
    findings,
    misplacedValue: findings.reduce((sum, f) => sum + f.value, 0),
    totalValue,
  };
}

// ---------------------------------------------------------------------------
// Headline findings
// ---------------------------------------------------------------------------

/**
 * The three things worth saying first.
 *
 * Everything below this line already exists elsewhere in the report. What this
 * section adds is ordering: a prospect reads the top of a document and skims
 * the rest, so the sentences they repeat to their spouse that night have to be
 * at the top, and they have to be the ones true of THIS household rather than
 * the ones we happen to enjoy saying.
 *
 * Rules-based and deterministic on purpose. No model, no generated language:
 * identical inputs give identical findings in identical order, because an
 * advisor has to defend every sentence and because two runs of one statement
 * must not produce two different documents.
 *
 * The severity numbers are a judgement about what a planner would lead with,
 * not a measurement of anything. They exist so the ranking is explicit and
 * reviewable in one place, rather than implied by the order somebody happened
 * to write the if-statements. Argue with the constants; that is what they are
 * for.
 */

// One finding per theme, so three findings are never three ways of saying the
// same thing. A portfolio that is concentrated, overlapping AND top-heavy has
// one concentration problem, not three.
const FINDING_THEMES = ['cashflow', 'tax', 'concentration', 'cost', 'location'];

export function getHeadlineFindings(inputs = {}, options = {}) {
  // `onePerTheme: false` keeps every finding rather than the strongest of each
  // subject: the meeting's pages and the brief list them all, while the
  // summary box wants three different observations (Jimmy, 2026-10-20).
  const { limit = 3, onePerTheme = true } = options;
  const { taxPools, summary, rmd, location, fees, lookThrough, assumptions, feeComparison } = inputs;

  const spending = toNumber(assumptions?.annualSpending);
  const candidates = [];
  const add = c => { if (c && c.severity > 0) candidates.push(c); };

  // --- cashflow -----------------------------------------------------------
  const depletionAge = summary?.coverage?.depletionAge;
  const depletionYear = summary?.coverage?.depletionYear;
  if (depletionAge || depletionYear) {
    // Nothing else in this document outranks running out of money.
    add({
      key: 'depletion', theme: 'cashflow', severity: 95,
      headline: depletionAge ? `Your savings run out at ${depletionAge}` : `Your savings run out in ${depletionYear}`,
      detail: 'On what you have told us, and before any investment growth is counted, the '
        + 'money stops covering the shortfall while you are still spending it.',
      section: 'income', sectionLabel: 'What comes in, against what you spend',
    });
  } else if (summary?.gapNow > 0 && spending > 0) {
    add({
      key: 'gapNow', theme: 'cashflow', severity: Math.min(80, (summary.gapNow / spending) * 100),
      headline: `${moneyish(summary.gapNow)} a year has to come out of savings`,
      detail: 'That is the difference between what you spend and the income that arrives '
        + 'whether you work or not. Every year, it is met by selling something.',
      section: 'income', sectionLabel: 'What comes in, against what you spend',
    });
  }

  // --- tax ----------------------------------------------------------------
  const deferredPct = toNumber(taxPools?.deferredSharePct);
  if (deferredPct >= 50) {
    add({
      key: 'deferredShare', theme: 'tax', severity: deferredPct,
      headline: `${Math.round(deferredPct)}% of your savings has never been taxed`,
      // No amount for a household already taking distributions: the figure
      // this used to quote was reconstructed from a schedule that started
      // years ago (see getRmdOutlook).
      detail: rmd && !rmd.alreadyStarted
        ? `From ${rmd.firstYear} the government sets the minimum you take out of it, starting `
          + `at ${moneyish(rmd.firstAmount)} a year of ordinary income.`
        : 'Money in that column is taxed as ordinary income on the way out, and eventually the '
          + 'timing of that withdrawal stops being your decision.',
      section: 'tax-horizon', sectionLabel: 'The part of the tax bill nobody has shown you',
    });
  }

  // --- concentration ------------------------------------------------------
  const top = lookThrough?.companies?.[0];
  if (top && top.weightPct >= 5) {
    add({
      key: 'singleCompany', theme: 'concentration', subject: top.name, severity: Math.min(90, top.weightPct * 7),
      headline: `${pctish(top.weightPct)} of your portfolio is one company`,
      detail: `Looking through every fund you hold, ${top.name} accounts for `
        + `${moneyish(top.value)}. Concentration that size is usually an accident of `
        + 'employment or of history rather than a decision anybody made.',
      section: 'holdings', sectionLabel: 'What you actually own',
    });
  }
  const spread = (lookThrough?.companies || []).reduce(
    (best, c) => (c.viaCount > (best?.viaCount || 0) ? c : best), null
  );
  if (spread && spread.viaCount >= 5) {
    add({
      key: 'overlap', theme: 'concentration', subject: spread.name, severity: Math.min(60, 30 + spread.viaCount * 3),
      headline: `${spread.viaCount} of your funds all hold the same company`,
      detail: `${spread.name} sits inside ${spread.viaCount} of your holdings. Owning a lot of `
        + 'funds is not the same as being diversified, when the funds own the same companies.',
      section: 'holdings', sectionLabel: 'What you actually own',
    });
  }

  // --- cost ---------------------------------------------------------------
  // Deliberately silent on a cheap portfolio. A weighted 0.15% is good news,
  // and manufacturing a fee complaint about it would cost more credibility
  // than the finding could ever be worth.
  const er = toNumber(fees?.weightedExpenseRatioPct);
  if (er >= 0.4) {
    add({
      key: 'feeDrag', theme: 'cost', severity: Math.min(75, er * 120),
      headline: `You are paying ${pctish(er, 2)} a year in fund costs`,
      detail: `That is ${moneyish(fees.annualFeeDollars)} a year, taken from inside the funds `
        + 'rather than billed to you, which is why it has never shown up on a statement.',
      section: 'fees', sectionLabel: 'What your investments cost you',
    });
  }
  // Preferred over the two below when it fires, because it is the strongest
  // thing that can be said about cost: not "this is expensive" by some outside
  // standard, but "you are already buying the cheap version of this yourself".
  if (feeComparison?.annualSaving > 0) {
    const worst = feeComparison.rows[0];
    add({
      key: 'feeSpread', theme: 'cost',
      severity: Math.min(78, feeComparison.annualSaving / 80),
      headline: `${moneyish(feeComparison.annualSaving)} a year more than the funds you already own`,
      detail: `${worst.ticker} charges ${pctish(worst.expenseRatioPct, 2)} while ${worst.benchmarkTicker}, `
        + `which is already in your portfolio, charges ${pctish(worst.benchmarkExpenseRatioPct, 2)}. `
        + `Across every holding priced this way that is ${moneyish(feeComparison.periodSaving)} over `
        + `${feeComparison.years} years, before any growth.`,
      section: 'fees', sectionLabel: 'What your investments cost you',
    });
  }

  const dear = (fees?.perHolding || [])
    .filter(h => toNumber(h.expenseRatioPct) >= 0.6 && toNumber(h.weightPct) >= 5)
    .sort((a, b) => (b.expenseRatioPct * b.weightPct) - (a.expenseRatioPct * a.weightPct))[0];
  if (dear) {
    add({
      key: 'expensiveHolding', theme: 'cost',
      severity: Math.min(70, dear.expenseRatioPct * 50 + dear.weightPct * 2),
      headline: `One fund is charging you ${pctish(dear.expenseRatioPct, 2)} a year`,
      // `feeDollars` is the holding's own cost; `annualFeeDollars` is the
      // portfolio total, which a holding does not carry, and reading it here
      // printed every fund as costing $0.
      // The cost of a fund is taken from the fund's own value, so it is never
      // a line on the statement, which is why people do not know they pay it
      // (Jimmy, 2026-10-01).
      detail: `${dear.name || dear.ticker} is ${pctish(dear.weightPct)} of your portfolio and costs `
        + `${moneyish(dear.feeDollars)} a year. That comes out of the fund's own value, so it never `
        + 'appears on a statement as a charge.',
      section: 'fees', sectionLabel: 'What your investments cost you',
    });
  }

  // --- asset location -----------------------------------------------------
  const misplaced = (location?.findings || [])[0];
  if (misplaced && misplaced.value >= 20000) {
    add({
      key: 'assetLocation', theme: 'location', severity: Math.min(50, misplaced.value / 2000),
      headline: `${moneyish(misplaced.value)} is sitting in the wrong account`,
      detail: `${misplaced.headline}. The investment itself is fine; where it is held is costing `
        + 'you tax for nothing in return.',
      section: 'tax-horizon', sectionLabel: 'The part of the tax bill nobody has shown you',
    });
  }

  // Best finding per theme, then the strongest themes, so what shows is three
  // different observations rather than one observation said three ways.
  if (!onePerTheme) {
    // Two findings about the same company say one thing twice: the stronger
    // one stays.
    const seen = new Set();
    return candidates
      .sort((a, b) => b.severity - a.severity)
      .filter(c => {
        if (!c.subject) return true;
        const id = `${c.theme}|${c.subject}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, limit);
  }
  return FINDING_THEMES
    .map(theme => candidates
      .filter(c => c.theme === theme)
      .sort((a, b) => b.severity - a.severity)[0])
    .filter(Boolean)
    .sort((a, b) => b.severity - a.severity)
    .slice(0, limit);
}

// Local formatters. The display components have their own; these exist so a
// finding's text is complete at the point it is decided, which is what lets the
// selector be tested without rendering anything.
function moneyish(n) {
  return `$${Math.round(toNumber(n)).toLocaleString('en-US')}`;
}
function pctish(n, digits = 1) {
  return `${toNumber(n).toFixed(digits)}%`;
}

// ---------------------------------------------------------------------------
// Roth conversion
// ---------------------------------------------------------------------------

/**
 * What converting some of the tax-deferred pool each year would do.
 *
 * This is the one lever in the document that changes the answer rather than
 * describing it, and it is the move Pure is actually for. The point is not that
 * conversion is free. It is that the client owns the timing right up until the
 * year RMDs begin, and after that the schedule owns it.
 *
 * THE COST IS SHOWN IN THE SAME UNITS AS THE BENEFIT, AND IS NEVER PRICED.
 * Converting is taxable income in the year it happens. Turning that into a tax
 * bill needs a marginal bracket that this questionnaire does not ask for, and
 * a rate we invented would be the one number on the page an advisor could not
 * defend in the meeting. So the panel reports the extra taxable income the
 * conversion creates, year by year, and leaves the bracket to the conversation.
 * A version of this that showed only the shrinking RMD would be a sales tool
 * rather than an assessment.
 *
 * Zero growth throughout, like the rest of the assessment.
 */
export function getRothConversionOutlook(rmd, plan = {}) {
  if (!rmd) return null;
  // Nothing to model once the withdrawals have started: the "before" figures a
  // comparison needs do not exist for that household, deliberately. The panel
  // has its own wording for this case.
  if (rmd.alreadyStarted) {
    return { windowYears: 0, windowClosesIn: null, annual: 0, years: 0, converted: 0,
      cappedByBalance: false, extraTaxableIncomePerYear: 0, extraTaxableIncomeTotal: 0,
      deferredAfter: rmd.balance, taxFreeAdded: 0 };
  }

  // The window closes when RMDs begin. Converting later is possible but it is a
  // different conversation, and the constraint is the teachable part: there are
  // only so many years left in which the timing is still a choice.
  const windowYears = Math.max(0, rmd.startAge - rmd.ageNow);

  const annual = Math.max(0, toNumber(plan.annual));
  const years = Math.max(0, Math.min(Math.round(toNumber(plan.years)), windowYears));
  const requested = annual * years;
  // Never convert more than exists. Clamping keeps the slider honest at its
  // top end rather than showing a balance driven below zero.
  const converted = Math.min(requested, rmd.balance);
  const cappedByBalance = requested > rmd.balance;

  const after = walkRmdSchedule(rmd.balance - converted, {
    startAge: rmd.startAge,
    ageNow: rmd.ageNow,
    currentYear: rmd.firstYear - (rmd.startAge - rmd.ageNow),
    horizonAge: rmd.horizonAge,
  });

  const firstAfter = after.schedule.length ? after.schedule[0].amount : 0;

  return {
    windowYears,
    windowClosesIn: rmd.firstYear,
    annual,
    years,
    converted,
    cappedByBalance,

    // What it costs, in the only unit we can state without inventing a rate.
    extraTaxableIncomePerYear: annual,
    extraTaxableIncomeTotal: converted,

    // What it buys.
    deferredAfter: Math.max(0, rmd.balance - converted),
    taxFreeAdded: converted,
    firstRmdBefore: rmd.firstAmount,
    firstRmdAfter: firstAfter,
    firstRmdReduction: Math.max(0, rmd.firstAmount - firstAfter),
    forcedBefore: rmd.totalToHorizon,
    forcedAfter: after.total,
    forcedReduction: Math.max(0, rmd.totalToHorizon - after.total),
    scheduleAfter: after.schedule,
  };
}

// ---------------------------------------------------------------------------
// Fee comparison
// ---------------------------------------------------------------------------

/**
 * What the same asset class costs elsewhere in their own portfolio.
 *
 * The comparison is deliberately SELF-REFERENTIAL. No curated list of "good"
 * funds, no benchmark rate we picked: the yardstick is the cheapest fund the
 * household already owns in the same broad asset class. That makes the finding
 * impossible to argue with on sourcing, needs nothing maintained, and is far
 * more persuasive than an outside reference, because the client is already
 * buying the cheap version of the thing they are overpaying for.
 *
 * WHAT THIS DOES NOT CLAIM. Two funds in one broad class are not
 * interchangeable: a large-cap growth fund and a large-cap blend index fund
 * both sit in US Equity and do different things. So the finding is never "swap
 * these", it is "you are paying very different prices inside the same asset
 * class, and here is what the spread costs". Whether the dearer fund earns its
 * price is the advisor's conversation, and the copy says so.
 *
 * Zero growth, like everything else here. The balance does not move, so the
 * twenty-year figure is the annual difference times twenty. Fee drag genuinely
 * compounds, which would make the real number larger; saying so is honest and
 * costs nothing, whereas picking a growth rate would put an assumption into the
 * one part of this report that has none.
 */

// A yardstick has to be a real position. A $400 holding at 0.02% is not
// evidence that the household can access that price at scale.
const MIN_BENCHMARK_VALUE = 25000;
// Below this the difference is noise, not a finding.
const MIN_GAP_PCT = 0.10;
// And below this the row costs more attention than the money is worth.
const MIN_ANNUAL_SAVING = 250;
const COMPARISON_YEARS = 20;
// Matches DEFAULT_ALLOCATION_BUCKET in the analysis service. It is the bucket
// for "we could not classify this", which makes it the one group whose members
// have nothing in common.
const UNCOMPARABLE_BUCKET = 'Other';

export function compareFeeAlternatives(bundle, options = {}) {
  const {
    minBenchmarkValue = MIN_BENCHMARK_VALUE,
    minGapPct = MIN_GAP_PCT,
    minAnnualSaving = MIN_ANNUAL_SAVING,
    years = COMPARISON_YEARS,
  } = options;

  // A holding with no published expense ratio is left out, not priced at zero.
  // `toNumber` turns a blank into 0, so without this check an individual stock
  // the data feed files under a style rather than as a single stock becomes
  // the 0.00% yardstick every fund in its class is measured against.
  const holdings = (bundle?.holdings || []).filter(
    h => h && h.expenseRatioPct !== null && h.expenseRatioPct !== undefined && h.expenseRatioPct !== ''
      && Number.isFinite(parseFloat(h.expenseRatioPct)) && toNumber(h.value) > 0
  );
  if (!holdings.length) return null;

  // The cheapest credible holding in each broad class becomes that class's
  // yardstick. A single stock has no expense ratio to compare against and is
  // excluded from both sides.
  // Two exclusions, both guarding the same category error.
  //
  // A holding with NO bucket is skipped rather than defaulted, because lumping
  // the unknown together is how a bond fund ends up measured against an equity
  // index fund. Bundles analyzed before `bucket` existed produce no comparison
  // until they are re-run, which is the safe direction.
  //
  // The catch-all bucket is skipped for the same reason and a stronger one:
  // things land in it precisely BECAUSE we could not tell what they are. A
  // target date fund and a commodity ETF share that bucket and share nothing
  // else, so a price comparison between them means nothing.
  const bucketed = holdings.filter(h => h.bucket && h.bucket !== UNCOMPARABLE_BUCKET);

  const yardsticks = new Map();
  bucketed.forEach(h => {
    if (h.assetClass === 'Single Stock') return;
    if (toNumber(h.value) < minBenchmarkValue) return;
    const bucket = h.bucket;
    const best = yardsticks.get(bucket);
    if (!best || toNumber(h.expenseRatioPct) < toNumber(best.expenseRatioPct)) {
      yardsticks.set(bucket, h);
    }
  });

  const rows = [];
  bucketed.forEach(h => {
    if (h.assetClass === 'Single Stock') return;
    const bucket = h.bucket;
    const yard = yardsticks.get(bucket);
    if (!yard || yard.ticker === h.ticker) return;

    const gap = toNumber(h.expenseRatioPct) - toNumber(yard.expenseRatioPct);
    if (gap < minGapPct) return;

    const annual = (toNumber(h.value) * gap) / 100;
    if (annual < minAnnualSaving) return;

    rows.push({
      ticker: h.ticker,
      name: h.name,
      bucket,
      assetClass: h.assetClass,
      value: toNumber(h.value),
      expenseRatioPct: toNumber(h.expenseRatioPct),
      benchmarkTicker: yard.ticker,
      benchmarkName: yard.name,
      benchmarkAssetClass: yard.assetClass,
      benchmarkExpenseRatioPct: toNumber(yard.expenseRatioPct),
      // True whenever the yardstick is a different kind of fund, which is most
      // of the time. The UI uses it to avoid implying a like-for-like swap.
      differentAssetClass: yard.assetClass !== h.assetClass,
      gapPct: gap,
      annualSaving: annual,
      periodSaving: annual * years,
    });
  });

  if (!rows.length) return null;
  rows.sort((a, b) => b.annualSaving - a.annualSaving);

  return {
    rows,
    years,
    annualSaving: rows.reduce((sum, r) => sum + r.annualSaving, 0),
    periodSaving: rows.reduce((sum, r) => sum + r.periodSaving, 0),
    valueInvolved: rows.reduce((sum, r) => sum + r.value, 0),
  };
}

// ---------------------------------------------------------------------------
// The goal sheet
// ---------------------------------------------------------------------------

/**
 * The one page of the old Word assessment that people actually read.
 *
 * It is reproduced here on purpose, numbering and wording and all, because an
 * advisor who has presented this for years should recognise it instantly and a
 * client who has seen one before should not have to relearn where the numbers
 * are. Everything else this report does is new; this part earns trust by being
 * familiar.
 *
 * WHAT IS FIXED RELATIVE TO THE WORD VERSION. Section 2 mixes two points in
 * time in the original: "income required at retirement" against income
 * assumptions that quietly include Social Security which has not started yet.
 * That is how the worked example shipped an $8,000 shortfall that contradicted
 * its own arithmetic. Here every line in section 2 is read from ONE year, and
 * the shortfall is literally the subtraction of the two lines above it, so the
 * page cannot disagree with itself.
 *
 * The year chosen is the first one in which the household is fully retired AND
 * every Social Security payment has started, which is the state the plan is
 * really about and the only state in which "combined Social Security income" is
 * a meaningful line. It is labelled with its year, so nobody has to guess.
 *
 * `+Tax+COLA` is Pure's own convention and is carried verbatim: the figure is
 * what the household needs to spend, before the tax owed on drawing it and
 * before the increases inflation will demand. Dropping it would understate
 * every requirement on the page.
 */
export function getGoalSheet(data, context = {}) {
  const { assumptions, timeline, taxPools, period, perRow = false } = context;
  if (!Array.isArray(timeline) || !timeline.length || !taxPools) return null;

  const pools = Object.fromEntries(taxPools.pools.map(p => [p.key, p.value]));
  const income = data?.income || {};
  const rowsOf = key => (Array.isArray(income[key]) ? income[key].filter(Boolean) : []);

  // Which year this sheet is read from.
  //
  // The steady state is everyone retired with every Social Security payment
  // running. The BRIDGE is the gap before that: retired, benefits not yet
  // started, living on the portfolio alone. It is the hardest period in most
  // plans and it is the one the advisor lead asked to lead with on 2026-09-03,
  // and it was exactly the period this sheet skipped. A household retiring at
  // 65 with benefits at 67 would see the shortfall for 2033 while the chart
  // beside it showed two years of a much larger gap.
  //
  // `Math.max(0, findIndex(...))` used to turn "no such year" into year zero,
  // so a household whose retirement could not be resolved was reported as
  // already retired with no shortfall. A failed search is now a stated
  // unresolved period, never today.
  //
  // Retired means the people have stopped working, not that no salary was
  // typed in. A year counts as retired only when employment income has stopped
  // AND everyone the questionnaire says is working has reached their recorded
  // retirement age. Without the second half, an employed 58-year-old whose
  // salary was left blank read as retired today, with a shortfall and a
  // depletion date built on that (QA review, 2026-09-30). Someone working with
  // no usable retirement age cannot be placed, and the sheet says so.
  const statusOf = who => String(data?.employment?.[who]?.status || '').trim();
  const isWorking = who => /employ/i.test(statusOf(who)) && !/not\s*employ|unemploy/i.test(statusOf(who));
  const AGE_KEY = { client1: 'c1Age', client2: 'c2Age' };
  const RETIRE_KEY = { client1: 'c1RetireAge', client2: 'c2RetireAge' };
  const workersRetired = row => ['client1', 'client2'].every(who => {
    if (!isWorking(who)) return true;
    // A second person with no age is not in the timeline at all.
    if (who === 'client2' && (row.c2Age === null || row.c2Age === undefined)) return true;
    const age = row[AGE_KEY[who]];
    const retireAge = toNumber(assumptions?.[RETIRE_KEY[who]]);
    return age !== null && age !== undefined && retireAge > 0 && age >= retireAge;
  });
  const isRetiredRow = r => r.bySource.employment === 0 && workersRetired(r);

  const maxSocialSecurity = timeline.reduce((max, r) => Math.max(max, r.bySource.socialSecurity), 0);
  const steadyIndex = timeline.findIndex(
    r => isRetiredRow(r) && r.bySource.socialSecurity >= maxSocialSecurity
  );
  const retiredIndex = timeline.findIndex(isRetiredRow);
  // A bridge exists only when they stop working before the benefits are all on.
  const bridgeIndex =
    retiredIndex >= 0 && steadyIndex >= 0 && retiredIndex < steadyIndex ? retiredIndex : -1;

  const startsRetired = isRetiredRow(timeline[0]);
  const wanted = period === 'steady' || period === 'bridge' ? period : null;
  let periodKey;
  if (wanted === 'steady' && steadyIndex >= 0) periodKey = 'steady';
  else if (wanted === 'bridge' && bridgeIndex >= 0) periodKey = 'bridge';
  else if (bridgeIndex >= 0) periodKey = 'bridge';       // default: lead with the bridge
  else if (steadyIndex >= 0) periodKey = 'steady';
  else periodKey = 'unresolved';

  const atIndex = periodKey === 'bridge' ? bridgeIndex : periodKey === 'steady' ? steadyIndex : -1;
  const now = timeline[0];
  const at = atIndex >= 0 ? timeline[atIndex] : now;

  // The bridge runs from the year they stop working to the year before the
  // last benefit starts, so the advisor can say what the whole gap costs
  // rather than quoting one year of it.
  const bridge = bridgeIndex >= 0
    ? {
        fromYear: timeline[bridgeIndex].year,
        toYear: timeline[steadyIndex].year - 1,
        years: steadyIndex - bridgeIndex,
        totalGap: timeline.slice(bridgeIndex, steadyIndex)
          .reduce((sum, r) => sum + Math.max(0, r.gap), 0),
      }
    : null;

  // Name a source when the household has exactly one of it, so a single rental
  // property reads as "Rental property" the way the Word sheet reads as "Real
  // estate income", and falls back to the category when there are several. The
  // AMOUNT always comes from the timeline, never from the rows, so a label can
  // never drift away from the figure beside it.
  const named = (key, fallback, noun) => {
    const rows = rowsOf(key);
    const described = rows.map(r => String(r.description || '').trim()).filter(Boolean);
    if (rows.length !== 1 || described.length !== 1) return fallback;
    // "Former employer" is what an advisor typed into a table cell; it is not a
    // line item. Adding the noun turns it back into one, unless the description
    // already carries it.
    const label = described[0];
    // Written `\\b` because inside a template a bare `\b` is a backspace
    // character, which never matched and printed "Pension pension".
    if (!noun || new RegExp(`\\b${noun}\\b`, 'i').test(label)) return label;
    return `${label} ${noun}`;
  };

  // Non-zero, not positive. A rental property running at a loss is real
  // income information and the timeline already carries it; dropping it here
  // was why this sheet could show $40,000 of income and a $60,000 shortfall
  // while the chart beside it showed $28,000 and $72,000 for the same year.
  // The meeting lists every pension and every other income on its own line,
  // under the title the questionnaire row carries, so an advisor preparing the
  // sheet can add a line ("Real estate income") or retitle one and still have
  // it total (Jimmy, 2026-10-03). A row is listed when it pays in the year the
  // sheet reads, or when it was started and has no amount yet, which is a
  // figure still missing rather than a zero. The amounts are the timeline's
  // own, so the lines always add up to the total beneath them.
  const hasText = v => typeof v === 'string' && v.trim() !== '';
  const rowLines = (key, fallback) => {
    const stored = Array.isArray(income[key]) ? income[key] : [];
    return (at.byRow?.[key] || [])
      .map(x => ({ ...x, row: stored[x.index] || {} }))
      .filter(x => x.amount !== 0 || (x.paying && hasText(x.row.description) && !hasText(String(x.row.annualAmount ?? ''))))
      // Its place in the table says which row it is. The row's bookkeeping id
      // is left out on purpose: it is made afresh on every load, and a
      // drawing is tied to exactly what the page shows.
      .map(x => ({
        key,
        index: x.index,
        label: String(x.row.description || '').trim() || fallback,
        amount: x.amount,
      }));
  };

  const sources = [];
  if (perRow) {
    sources.push(...rowLines('other', 'Other income'), ...rowLines('pension', 'Pension income'));
  } else {
    if (at.bySource.other !== 0) {
      sources.push({ key: 'other', label: named('other', 'Other income', 'income'), amount: at.bySource.other });
    }
    if (at.bySource.pension !== 0) {
      sources.push({ key: 'pension', label: named('pension', 'Pension income', 'pension'), amount: at.bySource.pension });
    }
  }
  if (at.bySource.employment !== 0) {
    sources.push({ key: 'employment', label: 'Employment income', amount: at.bySource.employment });
  }
  // Always last and always by this name, because it is the line every advisor
  // reads for first.
  sources.push({ key: 'socialSecurity', label: 'Combined Social Security income', amount: at.bySource.socialSecurity });

  // Off the timeline row, not re-added from the list above it. The list is for
  // display and can be filtered or relabelled; the total has to be the same
  // number the chart uses, by construction rather than by care.
  const totalIncome = at.totalIncome;

  return {
    liquid: {
      taxDeferred: pools.taxDeferred || 0,
      taxFree: pools.taxFree || 0,
      taxable: pools.taxable || 0,
      total: taxPools.total || 0,
    },
    income: {
      requiredToday: now.spendingNeed,
      requiredAtRetirement: at.spendingNeed,
      atYear: at.year,
      yearsAway: Math.max(0, at.year - now.year),
      // True only when they are retired in the first row of the timeline, which
      // is a fact about the household. It is no longer a side effect of a
      // search that found nothing.
      alreadyThere: periodKey !== 'unresolved' && atIndex === 0 && startsRetired,
      // What this sheet is reading from, so the page can say it out loud:
      // 'bridge' (retired, benefits not all started), 'steady' (everything
      // running), or 'unresolved' (we could not place them, and the figures
      // below are today's).
      period: periodKey,
      bridge,
      unresolvedRetirement: assumptions?.unresolvedRetirement || [],
      sources,
      totalIncome,
      // Subtraction of the two figures above it, and nothing else. If this ever
      // needs its own arithmetic, the page has started disagreeing with itself.
      shortfall: Math.max(0, at.spendingNeed - totalIncome),
    },
  };
}

// ---------------------------------------------------------------------------
// Where you stand: the four-quadrant summary
// ---------------------------------------------------------------------------

/**
 * The one-page reading of the household, in four boxes: what is working, what
 * needs attention, which levers are still available, and what happens if
 * nothing changes.
 *
 * This is the advisor lead's own mental model, stated in the 8/19 workflow
 * meeting: in every second assessment meeting he is silently asking "what's
 * really good about your situation, what's not so good, what are the risks if
 * you don't make any changes". He sketched it as a SWOT one-pager years ago.
 * The page never uses the acronym; clients get plain words.
 *
 * Two rules keep it honest. STRENGTHS ARE NEVER MANUFACTURED: each one has a
 * threshold, and a household that clears none of them gets an empty box rather
 * than a compliment, because a strength invented to balance the page devalues
 * the real ones. And EVERY LEVER CARRIES ITS PRICE OR ITS DEADLINE, because an
 * opportunity with no cost attached is an advert, not an observation.
 */
export function getSituationSummary(inputs = {}, options = {}) {
  const { taxPools, summary, rmd, location, fees, lookThrough, feeComparison, roth } = inputs;

  const working = [];
  const levers = [];
  const risks = [];

  // --- what is working ----------------------------------------------------
  const er = toNumber(fees?.weightedExpenseRatioPct);
  if (Number.isFinite(er) && er > 0 && er <= 0.25 && fees?.annualFeeDollars >= 0) {
    working.push({
      key: 'lowCost',
      headline: 'Your fund costs are genuinely low',
      detail: `A weighted ${er.toFixed(2)}% a year. Most portfolios we see cost several times that.`,
    });
  }
  if (summary?.coverage?.neverDepletes && summary?.gapNow > 0) {
    working.push({
      key: 'covered',
      headline: 'Your savings last through every year shown',
      detail: 'Even with zero growth assumed, the money covers every year of spending shown.',
    });
  }
  if (summary && summary.gapNow <= 0) {
    working.push({
      key: 'noGap',
      headline: 'Income already covers your spending',
      detail: 'What arrives each year meets what goes out, before touching savings.',
    });
  }
  const taxFreeShare = taxPools?.total > 0
    ? (taxPools.pools.find(p => p.key === 'taxFree')?.value || 0) / taxPools.total * 100
    : 0;
  if (taxFreeShare >= 20) {
    working.push({
      key: 'taxFreeShare',
      headline: `${Math.round(taxFreeShare)}% of your savings is already tax-free`,
      detail: 'That is unusual, and it gives you real flexibility in which dollars to spend first.',
    });
  }
  const top = lookThrough?.companies?.[0];
  if (top && top.weightPct < 5) {
    working.push({
      key: 'diversified',
      headline: 'No single company dominates your portfolio',
      detail: `Looking through every fund, the largest position is ${top.weightPct.toFixed(1)}%.`,
    });
  }

  // --- levers still available ---------------------------------------------
  if (roth && roth.windowYears > 0) {
    levers.push({
      key: 'rothWindow',
      headline: `${roth.windowYears} ${roth.windowYears === 1 ? 'year' : 'years'} left to move tax-deferred money on your terms`,
      detail: `Until ${roth.windowClosesIn}, conversion timing is your choice. The cost is income tax in the year you convert.`,
      section: 'tax-horizon',
    });
  }
  if (location?.misplacedValue >= 20000) {
    levers.push({
      key: 'assetLocation',
      headline: `${fmtMoneyish(location.misplacedValue)} could be held more tax-efficiently`,
      detail: 'Same investments, different accounts. Nothing is bought or sold except to move it.',
      section: 'tax-horizon',
    });
  }
  if (feeComparison?.annualSaving >= 1000) {
    levers.push({
      key: 'feeLevel',
      headline: `${fmtMoneyish(feeComparison.annualSaving)} a year at stake in fund pricing`,
      detail: 'Measured against the cheapest funds you already own, not against a list we picked.',
      section: 'fees',
    });
  }

  // --- if nothing changes -------------------------------------------------
  if (summary?.coverage?.depletionYear || summary?.coverage?.depletionAge) {
    risks.push({
      key: 'depletion',
      headline: summary.coverage.depletionYear
        ? `The savings run out in ${summary.coverage.depletionYear}`
        : `The savings run out at ${summary.coverage.depletionAge}`,
      detail: `${summary.coverage.depletionName && summary.coverage.depletionAge ? `When ${summary.coverage.depletionName} is ${summary.coverage.depletionAge}, and before` : 'Before'} any growth is counted. This is the figure planning exists to move.`,
      section: 'income',
    });
  }
  if (rmd) {
    risks.push({
      key: 'rmdTimer',
      headline: rmd.alreadyStarted
        ? 'Part of the tax-deferred pool is already being forced out each year'
        : `From ${rmd.firstYear}, ${fmtMoneyish(rmd.firstAmount)} a year comes out whether you want it or not`,
      detail: rmd.alreadyStarted
        ? 'Required minimum distributions, taxed as ordinary income. The amount is set per '
          + 'account from last year’s closing balance, so it is worth reading off the statements.'
        : 'Required minimum distributions, taxed as ordinary income, on the government’s schedule rather than yours.',
      section: 'tax-horizon',
    });
  }
  if (top && top.weightPct >= 9) {
    risks.push({
      key: 'concentrationRisk',
      headline: `${top.weightPct.toFixed(1)}% of the portfolio rides on one company`,
      detail: top.viaCount > 1
        ? `${top.name}, reached through ${top.viaCount} different holdings.`
        : `${top.name}, held directly.`,
      section: 'holdings',
    });
  }

  // ONE FACT, ONE BOX. The ranked findings draw on the same facts as the
  // levers and risks, and a summary that says "you are overpaying" in one box
  // and "money is at stake in pricing" in another is one observation wearing
  // two hats. Where a finding's fact already appears as a lever or a risk, the
  // lever or risk wins, because "here is what you can do" and "here is what
  // happens if you don't" are both more useful than "here is what is wrong".
  const covered = new Set();
  if (levers.some(l => l.key === 'feeLevel')) {
    covered.add('feeSpread'); covered.add('feeDrag'); covered.add('expensiveHolding');
  }
  if (levers.some(l => l.key === 'assetLocation')) covered.add('assetLocation');
  if (risks.some(r => r.key === 'concentrationRisk')) { covered.add('singleCompany'); covered.add('overlap'); }
  if (risks.some(r => r.key === 'rmdTimer')) covered.add('deferredShare');
  if (risks.some(r => r.key === 'depletion')) covered.add('depletion');

  // The summary box shows three, one per subject. The meeting and the brief
  // (options.only: the keys that have a page) take every finding that has
  // one: a finding with nowhere to go used to take one of the three places
  // and push out one that had (rules catalog, 2026-10-19).
  const only = options.only ? new Set(options.only) : null;
  const attention = getHeadlineFindings(inputs, { limit: 99, onePerTheme: !only })
    .filter(f => !covered.has(f.key))
    .filter(f => !only || only.has(f.key))
    .slice(0, only ? 99 : 3);

  return { working, attention, levers, risks };
}

function fmtMoneyish(n) {
  return `$${Math.round(toNumber(n)).toLocaleString('en-US')}`;
}

// ---------------------------------------------------------------------------
// Estate and asset preservation
// ---------------------------------------------------------------------------

/**
 * The fifth goal-sheet area, which the old packet covered with twenty pages of
 * boilerplate and this report had been skipping entirely.
 *
 * Everything here is a three-state reading, and the states are the point:
 *
 *   in-place  - the questionnaire records it exists
 *   none      - the advisor asked, and the client confirmed there isn't one
 *   unknown   - nobody has asked yet
 *
 * "None" and "unknown" get different sentences, because "you told us there is
 * no trust" is a fact about the household while "we haven't discussed a trust"
 * is a fact about the process, and printing the first when only the second is
 * true would put words in the client's mouth. This is exactly the distinction
 * the questionnaire's none-markers exist to carry.
 */
const ESTATE_DOCS = [
  { key: 'Trust', label: 'Living trust', why: 'Keeps your estate out of probate: no public record, no court timeline, no probate fees.' },
  { key: 'Will', label: 'Will', why: 'Directs whatever sits outside the trust, and names guardians where that matters.' },
  { key: 'FPOA', label: 'Financial power of attorney', why: 'Someone can act on your finances if you cannot, without a court appointing them.' },
  { key: 'MPOA', label: 'Medical power of attorney', why: 'Someone you chose makes medical decisions if you cannot.' },
];

const COVERAGES = [
  { none: 'LTC', types: ['LTC'], label: 'Long-term care coverage', why: 'A long illness is the one cost that can outrun a strong portfolio.' },
  { none: 'Umbrella', types: ['Umbrella/Liability'], label: 'Umbrella liability coverage', why: 'A lawsuit can reach past your policies to your savings; umbrella coverage sits over both.' },
  { none: 'Life', types: ['Term Life', 'Whole Life', 'Universal Life'], label: 'Life insurance', why: 'Replaces an income the household still depends on.' },
  { none: 'Disability', types: ['Disability'], label: 'Disability coverage', why: 'Protects the salary years retirement still counts on.' },
];

export function getProtectionChecklist(data) {
  const goals = data?.goals || {};
  // Codes whichever way the record spells or joins them (estateDocs.js).
  const estatePlan = estateDocCodes(goals.estatePlan);
  const estateAsked = estatePlan.length > 0;

  const policies = Array.isArray(data?.insurance?.policies)
    ? data.insurance.policies.filter(Boolean)
    : [];
  const noneOf = Array.isArray(data?.insurance?.noneOf) ? data.insurance.noneOf : [];

  const docs = ESTATE_DOCS.map(d => ({
    key: d.key,
    label: d.label,
    why: d.why,
    // The estate question lists every document, so an answered question that
    // leaves one unticked says it is not in place; an unanswered one says
    // nothing yet. (None counts as an answer.)
    status: estatePlan.includes(d.key) ? 'in-place' : estateAsked ? 'none' : 'unknown',
  }));
  const yearsEstablished = String(goals.yearsEstablished || '').trim() || null;

  const coverage = COVERAGES.map(c => {
    const held = policies.some(p => c.types.includes(String(p.type || '').trim()));
    return {
      key: c.none,
      label: c.label,
      why: c.why,
      status: held ? 'in-place' : noneOf.includes(c.none) ? 'none' : 'unknown',
    };
  });

  const all = [...docs, ...coverage];
  const counts = {
    inPlace: all.filter(i => i.status === 'in-place').length,
    none: all.filter(i => i.status === 'none').length,
    unknown: all.filter(i => i.status === 'unknown').length,
  };
  // Nothing asked and nothing recorded: render the section as an agenda rather
  // than as a wall of unknowns pretending to be findings.
  const anythingKnown = counts.inPlace + counts.none > 0;

  return { docs, coverage, yearsEstablished, counts, anythingKnown };
}

// ---------------------------------------------------------------------------
// The questions a full plan answers
// ---------------------------------------------------------------------------

/**
 * The close, structured.
 *
 * The advisor lead's criticism of the old assessment, verbatim: "you're just
 * tossing the questions out there... we're not actually answering any of those
 * questions". The fix is not to answer them here, because an assessment that
 * answered them would be a financial plan given away in the first meeting. The
 * fix is to ASK them precisely, in this household's terms, so the last page is
 * a list of decisions the prospect now knows they need to make and cannot make
 * from this document alone. That is the meeting's next step, written down.
 *
 * Each question only appears when the household actually faces it, and each
 * one names the number that makes it worth asking, because "when should you
 * take Social Security" is generic but "the timing moves a $114,000-a-year
 * decision" is theirs.
 */
export function getPlanQuestions(data, context = {}) {
  const { goalSheet, rmd, roth, taxPools, protection } = context;
  const income = data?.income || {};
  const rows = key => (Array.isArray(income[key]) ? income[key].filter(Boolean) : []);
  const questions = [];

  if (goalSheet) {
    questions.push({
      key: 'retire',
      phase: 'now',
      question: 'Can you retire when you want to, rather than when the money says you can?',
      because: 'Planning pins it down against real returns, not a zero-growth floor.',
    });
  }

  const ss = rows('socialSecurity');
  if (ss.length) {
    const total = ss.reduce((sum, r) => sum + toNumber(r.annualAmount), 0);
    questions.push({
      key: 'socialSecurity',
      phase: 'next',
      question: ss.length > 1
        ? 'When should each of you start Social Security?'
        : 'When should you start Social Security?',
      // A row with no amount says nothing about size (rules catalog,
      // 2026-10-19): "a $0-a-year decision" read as if there were no benefit.
      because: total > 0
        ? `The timing moves a ${fmtMoneyish(total)}-a-year decision that cannot be undone.`
        : 'The timing is a decision that cannot be undone, and the amount still needs recording.',
    });
  }

  if (rows('pension').length) {
    questions.push({
      key: 'pension',
      phase: 'next',
      question: 'Which pension option should you choose?',
      because: 'Single life, joint, or lump sum is a one-time election, and the right answer depends on everything above.',
    });
  }

  if (rmd && roth && roth.windowYears > 0) {
    questions.push({
      key: 'conversion',
      phase: 'next',
      question: 'How much should move to Roth each year, and in which tax brackets?',
      because: `The bracket arithmetic is the part planning works through, and the window closes in ${roth.windowClosesIn}.`,
    });
  }

  const filledPools = (taxPools?.pools || []).filter(p => p.value > 0).length;
  if (filledPools >= 2) {
    questions.push({
      key: 'withdrawalOrder',
      phase: 'later',
      question: 'Which account should each year of spending come from?',
      because: 'You have money in more than one tax treatment, and the order of withdrawal changes what you keep.',
    });
  }

  if (protection && (protection.counts.none > 0 || protection.counts.unknown > 0)) {
    const open = protection.counts.none + protection.counts.unknown;
    questions.push({
      key: 'protection',
      phase: 'now',
      question: 'What closes the gaps in the protection checklist?',
      because: `${open} ${open === 1 ? 'item is' : 'items are'} missing or undiscussed above, and each is cheapest to fix before it is needed.`,
    });
  }

  return questions;
}

// ---------------------------------------------------------------------------
// Style box
// ---------------------------------------------------------------------------

/**
 * The nine-box equity grid, which is the one piece of the Morningstar snapshot
 * advisors asked to keep.
 *
 * It earns its place because of one sentence an advisor can say off it: you are
 * retiring in three years and you are invested like someone with thirty years
 * to go. Neither the allocation donut nor the look-through can make that point,
 * because both answer "what do you own" rather than "what kind of risk is
 * inside it".
 *
 * Costs no new data. The classifier already resolves each holding to exactly
 * this vocabulary from YCharts fundamentals, so the grid is a regrouping of
 * something the report already computed.
 *
 * Non-US equity IS placed in the grid, the way Morningstar places it: the axes
 * are size and style, and geography is a different question that the allocation
 * bars answer. Anything with no size or style of its own stays OUT rather than
 * being guessed into a cell, and the section reports how much that was.
 */
const STYLE_SIZES = ['Large', 'Mid', 'Small'];
const STYLE_KINDS = ['Value', 'Blend', 'Growth'];

// Size and style both come from word membership, not from patterns. The
// canonical class list is inconsistent about the hyphen ("Large Cap Value" but
// "Mid-Cap Value"), and "Foreign Large Blend" carries a size with no "cap" at
// all, so matching whole words is both simpler and more forgiving than any
// regex that has to know those shapes.
const STYLE_SIZE_WORDS = { large: 'Large', mid: 'Mid', small: 'Small' };

export function parseStyle(assetClass) {
  const words = String(assetClass || '')
    .split(/[^A-Za-z]+/)
    .filter(Boolean)
    .map(w => w.toLowerCase());

  const sizeWord = words.find(w => STYLE_SIZE_WORDS[w]);
  const size = sizeWord ? STYLE_SIZE_WORDS[sizeWord] : null;
  const style = STYLE_KINDS.find(k => words.includes(k.toLowerCase())) || null;

  // Both axes or nothing. A holding with a size and no style has no cell, and
  // guessing one would put money in a box the classifier never claimed.
  return size && style ? { size, style } : null;
}

export function getStyleBox(bundle) {
  const holdings = (bundle?.holdings || []).filter(h => h && toNumber(h.value) > 0);
  if (!holdings.length) return null;

  const cells = new Map();
  const unplaced = new Map();
  let placedValue = 0;

  holdings.forEach(h => {
    const value = toNumber(h.value);
    const spot = parseStyle(h.assetClass);
    if (!spot) {
      const key = h.assetClass || 'Unclassified';
      unplaced.set(key, (unplaced.get(key) || 0) + value);
      return;
    }
    const key = `${spot.size}|${spot.style}`;
    cells.set(key, (cells.get(key) || 0) + value);
    placedValue += value;
  });

  if (!placedValue) return null;

  const grid = STYLE_SIZES.map(size => STYLE_KINDS.map(style => {
    const value = cells.get(`${size}|${style}`) || 0;
    return { size, style, value, pct: (value / placedValue) * 100 };
  }));

  const rowPct = Object.fromEntries(STYLE_SIZES.map((size, i) => [
    size, grid[i].reduce((sum, c) => sum + c.pct, 0),
  ]));
  const colPct = Object.fromEntries(STYLE_KINDS.map((style, j) => [
    style, grid.reduce((sum, row) => sum + row[j].pct, 0),
  ]));

  const totalValue = holdings.reduce((sum, h) => sum + toNumber(h.value), 0);
  const heaviest = grid.flat().reduce((a, b) => (b.value > a.value ? b : a), grid[0][0]);

  return {
    grid,
    rowPct,
    colPct,
    heaviest,
    placedValue,
    unplacedValue: totalValue - placedValue,
    coveragePct: (placedValue / totalValue) * 100,
    unplaced: [...unplaced.entries()]
      .map(([assetClass, value]) => ({ assetClass, value }))
      .sort((a, b) => b.value - a.value),
  };
}

/**
 * Where the timeline ends, and for whom: the year, the person who reaches
 * horizonAge in it, and the other person's age that year. The page says this
 * beside every "savings last" figure, because "beyond 95" means nothing until
 * it says whose 95.
 */
export function getHorizon(data, timeline, options = {}) {
  const { horizonAge = 95 } = options;
  const rows = Array.isArray(timeline) ? timeline.filter(Boolean) : [];
  if (!rows.length) return null;
  const family = data?.family || {};
  const last = rows[rows.length - 1];
  const people = [
    { name: (family.client1FirstName || '').trim() || 'You', age: toNumber(last.c1Age) },
    { name: (family.client2FirstName || '').trim() || 'Your spouse', age: toNumber(last.c2Age) },
  ].filter(p => p.age > 0);
  if (!people.length) return { year: last.year, horizonAge, name: null, age: null, other: null };
  const reaching = people.reduce((a, b) => (b.age < a.age ? b : a), people[0]);
  const other = people.find(p => p !== reaching) || null;
  return { year: last.year, horizonAge, name: reaching.name, age: reaching.age, other };
}

// Shared with cashFlowTimeline.js, which rules the whiteboard off the same
// timeline this file builds and must parse a figure or a date the same way.
export { toNumber, fullName, yearOf };
